import React, { useMemo, useState } from 'react';
import {
  Search, SlidersHorizontal, ChevronLeft, ChevronRight, MoreHorizontal, Sparkles, UserCheck,
} from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  BUCKET_ORDER, BUCKET_META, fmtXof, bucketOf, initials, avatarColor,
} from './recMeta';

const PAGE_SIZE = 8;

const MiniAgingCards = ({ bucketTotals = {}, bucketCounts = {} }) => (
  <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
    {BUCKET_ORDER.map((k) => {
      const m = BUCKET_META[k];
      return (
        <div key={k} className={`rounded-lg border p-2.5 ${m.card}`} data-testid={`member-aging-${k}`}>
          <div className={`text-[11px] font-semibold ${m.icon}`}>{m.label}</div>
          <div className={`text-sm font-bold tabular-nums ${m.value}`}>{fmtXof(bucketTotals[k] || 0)}</div>
          <div className="text-[11px] text-gray-500">{bucketCounts[k] || 0} facture{(bucketCounts[k] || 0) > 1 ? 's' : ''}</div>
        </div>
      );
    })}
  </div>
);

// Zone centrale : détail du membre / non-assignés + table des factures.
export const InvoiceTable = ({
  title, subtitle, avatarKey, data, mode = 'member',
  commercials = [], recommendedCommercialId, onOpenInvoice, onRelance, onEmailRelance, onAssignClient,
  headerActions, agingFilter, onAgingFilter,
}) => {
  const [q, setQ] = useState('');
  const [statusF, setStatusF] = useState('all');
  const [page, setPage] = useState(1);

  const all = useMemo(() => {
    const items = [];
    BUCKET_ORDER.forEach((k) => (data?.buckets?.[k] || []).forEach((inv) => items.push(inv)));
    return items.sort((a, b) => (b.days_overdue || 0) - (a.days_overdue || 0));
  }, [data]);

  const bucketCounts = useMemo(() => {
    const c = {};
    BUCKET_ORDER.forEach((k) => { c[k] = (data?.buckets?.[k] || []).length; });
    return c;
  }, [data]);

  const statuses = useMemo(() => Array.from(new Set(all.map((i) => i.status).filter(Boolean))), [all]);

  const filtered = useMemo(() => {
    let r = all;
    if (agingFilter) r = r.filter((i) => bucketOf(i.days_overdue) === agingFilter);
    if (statusF !== 'all') r = r.filter((i) => i.status === statusF);
    if (q) {
      const s = q.toLowerCase();
      r = r.filter((i) => `${i.invoice_ref} ${i.client_name} ${i.contract_number || ''}`.toLowerCase().includes(s));
    }
    return r;
  }, [all, agingFilter, statusF, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm" data-testid="rec-invoice-table">
      {/* Header membre */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
        <div className="flex items-center gap-3">
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${mode === 'unassigned' ? 'bg-amber-400' : avatarColor(avatarKey || title)}`}>
            {initials(title)}
          </span>
          <div>
            <h2 className="text-base font-semibold tracking-tight text-gray-900" data-testid="rec-detail-title">{title}</h2>
            <p className="text-xs text-gray-500">{subtitle}</p>
          </div>
        </div>
        {headerActions}
      </div>

      <div className="space-y-4 px-5 py-4">
        <MiniAgingCards bucketTotals={data?.bucketTotals} bucketCounts={bucketCounts} />

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[180px] flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Rechercher une facture, un client…" className="h-9 pl-8 text-xs" data-testid="rec-invoice-search" />
          </div>
          <Select value={agingFilter || 'all'} onValueChange={(v) => { onAgingFilter && onAgingFilter(v === 'all' ? null : v); setPage(1); }}>
            <SelectTrigger className="h-9 w-[140px] text-xs" data-testid="rec-aging-filter"><SelectValue placeholder="Ancienneté" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toute ancienneté</SelectItem>
              {BUCKET_ORDER.map((k) => <SelectItem key={k} value={k}>{BUCKET_META[k].label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusF} onValueChange={(v) => { setStatusF(v); setPage(1); }}>
            <SelectTrigger className="h-9 w-[130px] text-xs" data-testid="rec-status-filter"><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tout statut</SelectItem>
              {statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-9 text-xs" disabled><SlidersHorizontal className="mr-1 h-3.5 w-3.5" /> Filtres</Button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-gray-400">
                <th className="py-2 pr-2 text-left font-medium">Référence / Opération</th>
                <th className="px-2 py-2 text-left font-medium">Client</th>
                <th className="px-2 py-2 text-right font-medium">Montant dû</th>
                <th className="px-2 py-2 text-center font-medium">Ancienneté</th>
                <th className="px-2 py-2 text-center font-medium">Retard</th>
                <th className="px-2 py-2 text-center font-medium">Relances</th>
                <th className="px-2 py-2 text-left font-medium">{mode === 'unassigned' ? 'Assigner à' : 'Prochaine action'}</th>
                <th className="py-2 pl-2 text-right font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {pageItems.map((inv) => {
                const bk = bucketOf(inv.days_overdue);
                const m = BUCKET_META[bk];
                const late = (inv.days_overdue || 0) > 0;
                return (
                  <tr key={inv.invoice_ref} data-testid={`rec-invoice-row-${inv.invoice_ref}`} className="cursor-pointer transition-colors hover:bg-gray-50/70" onClick={() => onOpenInvoice(inv)}>
                    <td className="py-3 pr-2">
                      <div className="font-mono text-xs font-medium text-gray-800">{inv.invoice_ref}</div>
                      {inv.contract_number && <div className="font-mono text-[11px] text-gray-400">{inv.contract_number}</div>}
                    </td>
                    <td className="px-2 py-3 text-gray-700">{inv.client_name || '—'}</td>
                    <td className="px-2 py-3 text-right font-semibold tabular-nums text-gray-900">{fmtXof(inv.remaining)}</td>
                    <td className="px-2 py-3 text-center"><span className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ring-1 ${m.chip}`}>{m.label}</span></td>
                    <td className="px-2 py-3 text-center text-xs text-gray-600">{late ? `${inv.days_overdue} j` : `à échoir (${Math.abs(inv.days_overdue || 0)}j)`}</td>
                    <td className="px-2 py-3 text-center tabular-nums text-gray-500">{inv.reminder_count || 0}</td>
                    <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                      {mode === 'unassigned' ? (
                        <Select onValueChange={(v) => onAssignClient && onAssignClient(inv, v)}>
                          <SelectTrigger className="h-8 w-[160px] text-xs" data-testid={`rec-assign-select-${inv.invoice_ref}`}><SelectValue placeholder="Choisir…" /></SelectTrigger>
                          <SelectContent>
                            {commercials.map((c) => (
                              <SelectItem key={c.commercialId} value={c.commercialId}>
                                {c.commercialName} ({c.portfolioSize ?? c.count} cl.){c.commercialId === recommendedCommercialId ? ' · Recommandé' : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={(e) => { e.stopPropagation(); onRelance(inv); }} data-testid={`rec-relance-${inv.invoice_ref}`}>
                          {late ? 'Relancer' : 'Planifier'}
                        </Button>
                      )}
                    </td>
                    <td className="py-3 pl-2 text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"><MoreHorizontal className="h-4 w-4 text-gray-400" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onOpenInvoice(inv)}>Voir le détail</DropdownMenuItem>
                          {onEmailRelance && (
                            <DropdownMenuItem onClick={() => onEmailRelance(inv)} data-testid={`rec-row-email-${inv.invoice_ref}`}>
                              Envoyer une relance email
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => onRelance(inv)}>Logger une relance</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
              {pageItems.length === 0 && (
                <tr><td colSpan={8} className="py-12 text-center text-gray-400" data-testid="rec-invoice-empty">Aucune facture dans cette vue 🎉</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs text-gray-500">
            {filtered.length === 0 ? '0 facture' : `Affichage ${(safePage - 1) * PAGE_SIZE + 1} à ${Math.min(safePage * PAGE_SIZE, filtered.length)} sur ${filtered.length} factures`}
          </span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)} data-testid="rec-page-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white tabular-nums">{safePage}</span>
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)} data-testid="rec-page-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceTable;
